import { useState } from "react";
import { ErrorPanel, Skeleton } from "@/components/atoms";
import { EmptyState } from "@/components/molecules";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useOperator } from "@/hooks";
import { BookingTracker } from "./BookingTracker";
import { BookingsTableV2 } from "./BookingsTableV2";
import { BookingPanelDetails } from "./features/BookingPanelDetails";
import { useBookings } from "./hooks/useBookings";
import { useGetBookingsParams } from "./hooks/useGetBookingsParams";
import { useSelectedBookingId } from "./hooks/useSelectedBookingId";

export const BookingsPanel = () => {
  const {
    flags: { useOptimizedLayout },
  } = useOperator();
  const params = useGetBookingsParams();
  const { isLoading, bookings, error } = useBookings();
  // `?job=` deep links a booking from other pages (e.g. the Rating Manager). It only seeds the panel;
  // the open booking stays in local state.
  const [linkedBookingId, setLinkedBookingId] = useSelectedBookingId();
  const [selectedBookingId, setSelectedBookingId] = useState<string | undefined>(linkedBookingId ?? undefined);

  const handleClose = () => {
    setSelectedBookingId(undefined);
    if (linkedBookingId) setLinkedBookingId(undefined, "replaceIn");
  };

  if (error) return <ErrorPanel messages={getErrorMessages(error)} />;

  return (
    <>
      <div className="mt-2 overflow-auto p-1 lg:max-h-[calc(100vh-250px)]">
        {bookings.length < 1 && !params.search && !isLoading ? (
          <EmptyState title="Bookings" description="There are no bookings matching your selected date and filter criteria at the moment." />
        ) : isLoading ? (
          <Skeleton />
        ) : (
          <>
            <BookingsTableV2
              items={bookings}
              compactView={useOptimizedLayout}
              setSelectedBookingId={(id) => {
                if (linkedBookingId && id !== linkedBookingId) setLinkedBookingId(undefined, "replaceIn");
                if (selectedBookingId && id) {
                  setSelectedBookingId(undefined);
                  setTimeout(
                    () => {
                      setSelectedBookingId(id);
                    },
                    id === selectedBookingId ? 0 : 500
                  );
                } else {
                  setSelectedBookingId(id);
                }
              }}
              selectedBookingId={selectedBookingId}
            />
            <BookingTracker />
          </>
        )}
      </div>

      {!isLoading && <BookingsTableV2.Pagination />}

      <BookingPanelDetails id={selectedBookingId} open={!!selectedBookingId} onClose={handleClose} />
    </>
  );
};
